import { useRef } from 'react';
import { motion } from 'framer-motion';
import { ChapterMarker, RevealText } from './shared';

const CERTS = [
  {
    title: 'ISO 9001:2015 (QMS)',
    img: '/images/certs/iso_9001__2015_qms_audit_2026.jpg',
    pdf: '/docs/iso_9001__2015_qms_audit_2026.pdf',
    desc: 'Certification of our Quality Management System, guaranteeing consistent, premium-grade leather manufacturing.',
  },
  {
    title: 'ISO 14001:2015 (EMS)',
    img: '/images/certs/iso_14001__2015_ems_audit_2026.jpg',
    pdf: '/docs/iso_14001__2015_ems_audit_2026.pdf',
    desc: 'Certification of our Environmental Management System, ensuring strict adherence to global environmental standards.',
  },
  {
    title: 'SMETA 4-Pillar Audit',
    img: '/images/certs/smeta_sedex_4pillar_full_aduit_report.jpg',
    pdf: '/docs/smeta_sedex_4pillar_full_aduit_report.pdf',
    desc: 'A comprehensive Sedex ethical trade audit verifying our rigorous labor, safety, and business ethics compliance.',
  },
  {
    title: 'Energy Audit 2026',
    img: '/images/certs/energy_audit__2026.jpg',
    pdf: '/docs/energy_audit__2026.pdf',
    desc: 'An assessment of our energy consumption, highlighting our commitment to sustainable operations and reducing our carbon footprint.',
  },
];

/* ── Document Tilt Card ─────────────────────────────────────── */
function DocCard({ cert }) {
  const cardRef = useRef(null);

  const handleMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = ((y - centerY) / centerY) * -10;
    const rotateY = ((x - centerX) / centerX) * 10;
    card.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  };

  const handleLeave = () => {
    const card = cardRef.current;
    if (card) card.style.transform = 'perspective(800px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
  };

  return (
    <div className="flex flex-col">
      <a
        href={cert.pdf}
        target="_blank"
        rel="noreferrer"
        className="block relative group overflow-hidden border border-[#2A2A2A] aspect-[1/1.4] mb-5 bg-[#111111]"
      >
        <div
          ref={cardRef}
          onMouseMove={handleMove}
          onMouseLeave={handleLeave}
          className="w-full h-full"
          style={{ transition: 'transform 0.2s ease-out', willChange: 'transform', transformStyle: 'preserve-3d' }}
        >
          <img
            src={cert.img}
            alt={cert.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 opacity-80 group-hover:opacity-100"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent opacity-80" />
          
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#0A0A0A]/40 backdrop-blur-sm">
            <span className="font-accent text-xs text-[#F5F5F0] tracking-[0.2em] border border-[#F5F5F0]/30 px-6 py-3 rounded-full bg-[#0A0A0A]/60">
              VIEW DOCUMENT
            </span>
          </div>
        </div>
      </a>
      <h3 className="font-display text-2xl font-light text-[#F5F5F0] mb-2">{cert.title}</h3>
      <p className="font-body font-light text-xs text-[#A3A3A3] leading-relaxed">{cert.desc}</p>
    </div>
  );
}

export default function Ch11About() {
  return (
    <section id="ch-11" data-testid="chapter-11" className="relative bg-[#0d0b09] px-6 md:px-16 py-28 md:py-40 overflow-hidden border-t border-[#2A2A2A]">
      <ChapterMarker num="09" title="About the Company" />
      
      <div className="max-w-4xl mb-20">
        <RevealText className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight mb-8">
          Crafting legacy through<br /><span className="italic text-[#B89B72]">uncompromising standards.</span>
        </RevealText>
        <motion.p 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-body font-light text-sm md:text-base text-[#A3A3A3] leading-relaxed max-w-2xl"
        >
          For over three decades, PT Exports has redefined premium leather manufacturing by blending traditional craftsmanship with sustainable innovation. Partnering with the world's most demanding fashion houses, we ensure every garment we create is backed by the highest international standards of quality, ethics, and environmental responsibility.
        </motion.p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 mt-16">
        {CERTS.map((cert, i) => (
          <motion.div
            key={cert.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: i * 0.15 }}
          >
            <DocCard cert={cert} />
          </motion.div>
        ))}
      </div>
    </section>
  );
}
